import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Save, X, BookOpen, FileText, Inbox, ArrowLeft,
} from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";

const EMPTY_COURSE = {
  title: "", category: "Reference Management", type: "recorded", level: "Beginner",
  duration: "", modules: "", price_note: "Enquire for pricing", thumbnail: "", description: "",
};

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-2.5 text-sm font-sans text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-cherry/40";
const labelCls = "block text-[11px] font-mono uppercase tracking-[0.2em] text-navy/50 mb-1.5";

function Login({ onSuccess }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      localStorage.setItem("sv_token", data.token);
      onSuccess(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="ScholarVerse LAB" className="h-11 w-11 rounded-full" />
          <div>
            <p className="font-serif text-lg font-bold text-navy leading-tight">ScholarVerse LAB</p>
            <p className="text-[11px] font-mono uppercase tracking-widest text-cherry">Admin Panel</p>
          </div>
        </div>
        <label className={labelCls}>Email</label>
        <input
          type="email" required value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={inputCls} placeholder="admin@scholarverselab.com" data-testid="admin-email-input"
        />
        <div className="mt-4">
          <label className={labelCls}>Password</label>
          <input
            type="password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputCls} placeholder="••••••••" data-testid="admin-password-input"
          />
        </div>
        {error && <p className="mt-3 text-sm text-cherry" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit" disabled={loading} data-testid="admin-login-submit"
          className="mt-6 w-full bg-cherry hover:bg-[#B81919] disabled:opacity-60 text-white font-medium py-3 rounded-full transition-colors"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <Link to="/" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-navy/50 hover:text-navy transition-colors">
          <ArrowLeft className="h-3 w-3" /> Back to site
        </Link>
      </form>
    </div>
  );
}

function CourseForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      className="bg-chalk/60 border border-navy/10 rounded-2xl p-6 space-y-4"
      data-testid="course-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Course Title</label>
          <input required value={form.title} onChange={set("title")} className={inputCls} data-testid="course-form-title" />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <input required value={form.category} onChange={set("category")} className={inputCls} data-testid="course-form-category" />
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select value={form.type} onChange={set("type")} className={inputCls} data-testid="course-form-type">
            <option value="recorded">Recorded</option>
            <option value="live">Live Class</option>
            <option value="workshop">Workshop</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Level</label>
          <input value={form.level} onChange={set("level")} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Duration</label>
          <input value={form.duration} onChange={set("duration")} className={inputCls} placeholder="Self-Paced / 2 Days" />
        </div>
        <div>
          <label className={labelCls}>Modules</label>
          <input value={form.modules} onChange={set("modules")} className={inputCls} placeholder="12 Video Modules" />
        </div>
        <div>
          <label className={labelCls}>Price Note</label>
          <input value={form.price_note} onChange={set("price_note")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Thumbnail Image URL</label>
          <input value={form.thumbnail} onChange={set("thumbnail")} className={inputCls} placeholder="https://..." data-testid="course-form-thumbnail" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Description</label>
          <textarea rows={3} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} data-testid="course-form-save"
          className="inline-flex items-center gap-2 bg-navy hover:bg-cherry text-white text-sm font-medium px-6 py-2.5 rounded-full transition-colors disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Course"}
        </button>
        <button type="button" onClick={onCancel} data-testid="course-form-cancel"
          className="inline-flex items-center gap-2 border border-navy/20 text-navy text-sm font-medium px-6 py-2.5 rounded-full hover:bg-navy/5 transition-colors">
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>
    </form>
  );
}

function CoursesTab({ courses, reload }) {
  const [editing, setEditing] = useState(null); // null | "new" | course object
  const [saving, setSaving] = useState(false);

  const save = async (form) => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/courses", form);
      else await api.put(`/admin/courses/${editing.id}`, form);
      toast.success("Course saved");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save course");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (course) => {
    if (!window.confirm(`Delete "${course.title}"?`)) return;
    try {
      await api.delete(`/admin/courses/${course.id}`);
      toast.success("Course deleted");
      reload();
    } catch {
      toast.error("Could not delete course");
    }
  };

  return (
    <div className="space-y-5">
      {editing === null ? (
        <button onClick={() => setEditing("new")} data-testid="admin-add-course-button"
          className="inline-flex items-center gap-2 bg-cherry hover:bg-[#B81919] text-white text-sm font-medium px-6 py-3 rounded-full transition-colors">
          <Plus className="h-4 w-4" /> Add New Course
        </button>
      ) : (
        <CourseForm
          initial={editing === "new" ? EMPTY_COURSE : editing}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}

      <div className="space-y-3">
        {courses.map((c) => (
          <div key={c.id} data-testid={`admin-course-row-${c.id}`}
            className="flex items-center gap-4 bg-white border border-navy/10 rounded-xl p-4">
            <img src={c.thumbnail} alt="" className="h-14 w-20 object-cover rounded-lg bg-chalk shrink-0 hidden sm:block" />
            <div className="min-w-0 flex-1">
              <p className="font-serif font-semibold text-navy truncate">{c.title}</p>
              <p className="text-xs font-mono uppercase tracking-widest text-navy/50 mt-0.5">
                {c.type} · {c.category}
              </p>
            </div>
            <button onClick={() => setEditing(c)} data-testid={`admin-edit-course-${c.id}`}
              className="p-2 text-navy/60 hover:text-navy hover:bg-chalk rounded-lg transition-colors" aria-label="Edit">
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={() => remove(c)} data-testid={`admin-delete-course-${c.id}`}
              className="p-2 text-cherry/70 hover:text-cherry hover:bg-cherry/10 rounded-lg transition-colors" aria-label="Delete">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {courses.length === 0 && <p className="text-sm text-navy/50">No courses yet. Add your first one above.</p>}
      </div>
    </div>
  );
}

const TEXT_FIELDS = [
  ["hero_overline", "Hero Overline"],
  ["hero_subtitle", "Hero Subtitle", true],
  ["about_overline", "About Overline"],
  ["about_title", "About Title"],
  ["about_description", "About Description", true],
  ["about_description_2", "About Second Paragraph", true],
  ["founder_overline", "Founder Overline"],
  ["founder_title", "Founder Title"],
  ["founder_name", "Founder Name"],
  ["founder_role", "Founder Role"],
  ["founder_story", "Founder Story", true],
  ["founder_quote", "Founder Quote", true],
  ["founder_photo", "Founder Photo URL"],
  ["workshop_overline", "Workshop Overline"],
  ["workshop_title", "Workshop Title"],
  ["workshop_duration", "Workshop Duration"],
  ["workshop_dates", "Workshop Dates"],
  ["workshop_form_url", "Google Form URL"],
  ["workshop_note", "Workshop Note", true],
  ["workshop_price_label", "Workshop Price Label"],
  ["workshop_price_current", "Workshop Current Price (e.g. ₹499)"],
  ["workshop_price_future", "Workshop Future Price (e.g. ₹699)"],
  ["co_founder_name", "Co-Founder Name"],
  ["co_founder_role", "Co-Founder Role"],
  ["co_founder_quote", "Co-Founder Quote", true],
  ["co_founder_photo", "Co-Founder Photo URL"],
  ["cta_title", "CTA Title"],
  ["cta_subtitle", "CTA Subtitle", true],
  ["whatsapp", "WhatsApp Number (with country code)"],
  ["email", "Contact Email"],
  ["workshop_day1_title", "Workshop Day 1 Title"],
  ["workshop_day2_title", "Workshop Day 2 Title"],
  ["udyam_id", "Udyam / MSME ID"],
  ["logo_url", "Site Logo URL (leave empty for default)"],
  ["linkedin_url", "LinkedIn Profile URL"],
  ["whatsapp_group_url", "WhatsApp Community Group Link"],
];

const LIST_FIELDS = [
  ["community_items", "Community Love entries (Name | Role | Quote, one per line)"],
  ["workshop_day1_items", "Workshop Day 1 Curriculum (one per line)"],
  ["workshop_day2_items", "Workshop Day 2 Curriculum (one per line)"],
  ["hero_title_lines", "Hero Headline Lines (one per line)"],
  ["hero_images", "Hero Slideshow Image URLs (one per line)"],
  ["marquee_items", "Marquee Items (one per line)"],
  ["workshop_highlights", "Workshop Highlights (one per line)"],
];

function ContentTab({ content, reload }) {
  const [form, setForm] = useState(null);
  const [pillars, setPillars] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setForm({ ...content });
      setPillars((content.pillars || []).map((p) => `${p.title} | ${p.desc}`).join("\n"));
    }
  }, [content]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    const payload = { ...form };
    for (const [key] of LIST_FIELDS) {
      payload[key] = (form[key] || []).filter(Boolean);
    }
    payload.pillars = pillars
      .split("\n")
      .map((line) => line.split("|").map((s) => s.trim()))
      .filter(([t]) => t)
      .map(([title, desc]) => ({ title, desc: desc || "" }));
    try {
      await api.put("/admin/content", payload);
      toast.success("Site content updated");
      reload();
    } catch {
      toast.error("Could not save content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="content-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TEXT_FIELDS.map(([key, label, area]) => (
          <div key={key} className={area ? "sm:col-span-2" : ""}>
            <label className={labelCls}>{label}</label>
            {area ? (
              <textarea rows={3} value={form[key] || ""} onChange={set(key)} className={`${inputCls} resize-none`} data-testid={`content-${key}`} />
            ) : (
              <input value={form[key] || ""} onChange={set(key)} className={inputCls} data-testid={`content-${key}`} />
            )}
          </div>
        ))}
        {LIST_FIELDS.map(([key, label]) => (
          <div key={key} className="sm:col-span-2">
            <label className={labelCls}>{label}</label>
            <textarea
              rows={4}
              value={(form[key] || []).join("\n")}
              onChange={(e) => setForm({ ...form, [key]: e.target.value.split("\n") })}
              className={`${inputCls} resize-y font-mono text-xs`}
              data-testid={`content-${key}`}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label className={labelCls}>About Pillars (Title | Description, one per line)</label>
          <textarea rows={4} value={pillars} onChange={(e) => setPillars(e.target.value)} className={`${inputCls} resize-y font-mono text-xs`} data-testid="content-pillars" />
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="content-save-button"
        className="inline-flex items-center gap-2 bg-navy hover:bg-cherry text-white text-sm font-medium px-8 py-3 rounded-full transition-colors disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save All Content"}
      </button>
    </div>
  );
}

function EnquiriesTab({ enquiries, reload }) {
  const remove = async (enq) => {
    if (!window.confirm(`Delete enquiry from ${enq.name}?`)) return;
    try {
      await api.delete(`/admin/enquiries/${enq.id}`);
      toast.success("Enquiry deleted");
      reload();
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  if (enquiries.length === 0)
    return <p className="text-sm text-navy/50" data-testid="enquiries-empty">No enquiries yet. They will appear here when visitors use the Enquire button.</p>;
  return (
    <div className="space-y-3" data-testid="enquiries-list">
      {enquiries.map((enq, i) => (
        <div key={enq.id || i} className="bg-white border border-navy/10 rounded-xl p-5" data-testid={`enquiry-row-${i}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-serif font-semibold text-navy">{enq.name}</p>
            <div className="flex items-center gap-3">
              <p className="text-[11px] font-mono uppercase tracking-widest text-cherry">{enq.course}</p>
              <button onClick={() => remove(enq)} data-testid={`enquiry-delete-${i}`}
                className="p-2 text-cherry/70 hover:text-cherry hover:bg-cherry/10 rounded-lg transition-colors" aria-label="Delete enquiry">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-1 text-xs font-sans text-navy/60">{enq.email}{enq.phone ? ` · ${enq.phone}` : ""}</p>
          {enq.message && <p className="mt-2 text-sm font-sans text-[#5B6B7C]">{enq.message}</p>}
          <p className="mt-2 text-[11px] font-mono text-navy/40">{new Date(enq.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

const TABS = [
  ["courses", "Courses", BookOpen],
  ["content", "Site Content", FileText],
  ["enquiries", "Enquiries", Inbox],
];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [content, setContent] = useState(null);
  const [enquiries, setEnquiries] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [c, ct, en] = await Promise.all([
        api.get("/courses"), api.get("/content"), api.get("/admin/enquiries"),
      ]);
      setCourses(c.data);
      setContent(ct.data);
      setEnquiries(en.data);
    } catch {
      /* handled per-tab */
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("sv_token");
    if (!token) return setUser(null);
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => {
        localStorage.removeItem("sv_token");
        setUser(null);
      });
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  if (user === undefined) return <div className="min-h-screen bg-navy" />;
  if (user === null) return <Login onSuccess={setUser} />;

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("sv_token");
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-chalk" data-testid="admin-panel">
      <header className="bg-navy text-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-9 w-9 rounded-full bg-white" />
            <div>
              <p className="font-serif font-bold leading-tight">ScholarVerse <span className="text-cherry">LAB</span></p>
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/50">Content Manager</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors hidden sm:block">
              View Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs font-medium border border-white/25 hover:border-white/60 px-4 py-2 rounded-full transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-full border whitespace-nowrap transition-colors ${
                tab === key ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-navy/15 hover:border-navy/40"
              }`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "courses" && <CoursesTab courses={courses} reload={loadData} />}
        {tab === "content" && <ContentTab content={content} reload={loadData} />}
        {tab === "enquiries" && <EnquiriesTab enquiries={enquiries} reload={loadData} />}
      </main>
    </div>
  );
}
